import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import styled from '@emotion/styled';
import { Paper } from '@mui/material';
import { FORTUNE_WHEEL_CONSTANTS } from '../constants';
import { Part, Player } from '../types';
import { createWheel, withRepartition } from '../utils';
import arrowImage from '../static/arrow.png';
import CartoonButton from './CartoonButton.component';
import WinnerAnnouncement from './WinnerAnnouncement.component';

const {
  wheelRadius,
  wheelOuterLineWidth,
  wheelOuterLineColor,
} = FORTUNE_WHEEL_CONSTANTS;

const WheelContainer = styled(Paper)({
  width: `${2 * wheelRadius}px`,
  height: `${2 * wheelRadius}px`,
  borderRadius: '50%',
  border: `${wheelOuterLineWidth}px solid ${wheelOuterLineColor}`,
  overflow: 'hidden' as const,
  position: 'relative' as const,
  boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)',
});

interface FortuneWheelProps {
  players?: Player[];
  phase?: 'waiting' | 'countdown' | 'spinning' | 'result';
  spinConfig?: { finalAngle: number; duration: number } | null;
  isAdmin?: boolean;
  onSpinClick?: () => void;
  onResetClick?: () => void;
  winner?: Player | null;
  isWelcome?: boolean;
  welcomeParts?: Part[];
  currentPlayerId?: string;
  adminId?: string;
  minigameActive?: boolean;
}

const FortuneWheel: React.FC<FortuneWheelProps> = ({
  players = [],
  phase = 'waiting',
  spinConfig = null,
  isAdmin = false,
  onSpinClick,
  onResetClick,
  winner = null,
  isWelcome = false,
  welcomeParts = [],
  currentPlayerId,
  adminId,
  minigameActive = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const arrowRef = useRef<HTMLImageElement>(null);
  // Track the current CSS rotation so we can reset it cleanly
  const currentRotationRef = useRef<number>(0);
  // Track ongoing spin timeout ids for cleanup
  const spinTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Inject named @keyframes for idle animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes wheelIdleRock {
        0%, 100% { transform: rotate(-3deg); }
        50% { transform: rotate(3deg); }
      }
      @keyframes arrowBounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      @keyframes welcomeSpin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // Idle rock + arrow bounce when waiting; cleared by spin effect when spinning
  useEffect(() => {
    if (isWelcome) return;
    const canvas = canvasRef.current;
    if (canvas && phase === 'waiting') {
      canvas.style.animation = 'wheelIdleRock 3s ease-in-out infinite';
      canvas.style.transition = '';
      canvas.style.transform = '';
      currentRotationRef.current = 0;
    }
    if (arrowRef.current) {
      arrowRef.current.style.animation = phase === 'waiting'
        ? 'arrowBounce 1.2s ease-in-out infinite'
        : 'none';
    }
  }, [phase, isWelcome]);

  // Convert players → parts for the canvas
  const parts: Part[] = isWelcome
    ? welcomeParts
    : players.map(p => ({
        id: p.id,
        name: p.name,
        color: p.color,
        weight: p.coefficient,
      }));

  const partsWithRepartition = React.useMemo(() => withRepartition(parts), [parts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draw the wheel whenever parts change
  useEffect(() => {
    createWheel(canvasRef, partsWithRepartition);
  }, [partsWithRepartition]);

  // Draw player emoji/avatars with current-player highlight and admin crown
  useEffect(() => {
    if (isWelcome || !canvasRef.current || partsWithRepartition.length === 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const center = wheelRadius;
    const emojiRadius = wheelRadius * 0.62;

    partsWithRepartition.forEach((part, idx) => {
      const player = players[idx];
      if (!player) return;
      const midAngle = (part.startAngle + part.endAngle) / 2;
      const x = center + emojiRadius * Math.cos(midAngle);
      const y = center + emojiRadius * Math.sin(midAngle);

      // Emoji or custom avatar
      if (player.avatarType === 'bundled' && player.avatar) {
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.font = '30px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(player.avatar, x, y);
        ctx.fillText(player.avatar, x, y);
        ctx.restore();
      } else if (player.avatarType === 'custom' && player.avatar) {
        const img = new Image();
        img.onload = () => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(x, y, 18, 0, 2 * Math.PI);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, x - 18, y - 18, 36, 36);
          ctx.restore();
        };
        img.src = player.avatar;
      }

    });
  }, [partsWithRepartition, players, isWelcome, currentPlayerId, adminId]);

  // Spin animation: watch phase + spinConfig
  useEffect(() => {
    if (phase !== 'spinning' || !spinConfig || !canvasRef.current) return;

    const canvas = canvasRef.current;

    // Clear any pending timeouts from a previous spin
    spinTimeoutsRef.current.forEach(id => clearTimeout(id));
    spinTimeoutsRef.current = [];

    const { finalAngle, duration } = spinConfig;

    // Always reset to 0 before each spin so finalAngle is an absolute target
    canvas.style.animation = 'none';
    canvas.style.transition = 'none';
    canvas.style.transform = 'rotate(0rad)';
    void canvas.getBoundingClientRect(); // force reflow
    currentRotationRef.current = 0;

    // Single-phase easeOutExpo: starts fast, decelerates smoothly to a stop.
    // Two-phase approaches cause a visible re-acceleration at the phase boundary.
    canvas.style.transition = `transform ${duration / 1000}s cubic-bezier(0.08, 0.85, 0.2, 1)`;
    canvas.style.transform = `rotate(${finalAngle}rad)`;
    currentRotationRef.current = finalAngle;

    return () => {
      spinTimeoutsRef.current.forEach(id => clearTimeout(id));
      spinTimeoutsRef.current = [];
    };
  }, [phase, spinConfig]);

  if (isWelcome) {
    return (
      <WheelContainer>
        <canvas
          ref={canvasRef}
          width={2 * wheelRadius}
          height={2 * wheelRadius}
          style={{ animation: 'welcomeSpin 12s linear infinite' }}
        />
      </WheelContainer>
    );
  }

  return (
    <Box
      component="section"
      display="flex"
      position="relative"
      flexDirection="column"
      height="100%"
      justifyContent="center"
      alignItems="center"
      gap="16px"
      paddingTop="16px"
    >
      <img ref={arrowRef} width={wheelRadius / 3.5} src={arrowImage} alt="Wheel arrow pointer" />
      <Box sx={{ mb: 2 }}>
        <WheelContainer>
          <canvas ref={canvasRef} width={2 * wheelRadius} height={2 * wheelRadius} />
          {phase === 'waiting' && isAdmin && !minigameActive && (
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
              <CartoonButton
                variant="contained"
                onClick={onSpinClick}
                sx={{ background: '#3f51b5', borderRadius: '50%', width: 80, height: 80, minWidth: 0, fontSize: '0.7rem', fontWeight: 'bold', lineHeight: 1.2, p: 0 }}
              >
                SPIN
              </CartoonButton>
            </Box>
          )}
        </WheelContainer>
      </Box>

      {phase === 'result' && winner && (
        <WinnerAnnouncement
          winner={winner}
          isAdmin={isAdmin}
          onSpinAgain={isAdmin ? onResetClick : undefined}
        />
      )}
    </Box>
  );
};

export default FortuneWheel;
